
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Lock, CheckCircle, ChevronRight, Download, BookOpen, Star, User, Mail, Phone, MapPin, GraduationCap, Shield, Clock, Award } from 'lucide-react';
import { COURSE_PRICE, CV_DOWNLOAD_PRICE } from './PaymentModal';
import PaymentModal from './PaymentModal';

interface UserDashboardProps {
  user: any;
  skillsList: any[];
  enrolledCourses: string[];
  selectedSkills: string[];
  hasPaidForCV: boolean;
  onEnrollClick: (courseIds: string[]) => void;
  onPaymentSuccess: (txRef: string, courses: string[], courseNames: string[]) => void;
  setPaymentModalOpen: (open: boolean) => void;
  currentStep: number;
  activeTab: string;
  setActiveTab: (tab: 'register' | 'skills' | 'dashboard' | 'cv' | 'admin') => void;
}

interface EnrollmentRecord {
  tx_ref: string;
  courses: string[];
  paidAt: string;
  amount: number;
  email: string;
}

const UserDashboard: React.FC<UserDashboardProps> = ({
  user,
  skillsList,
  enrolledCourses,
  selectedSkills,
  hasPaidForCV,
  onEnrollClick,
  onPaymentSuccess,
  setPaymentModalOpen,
  currentStep,
  activeTab,
  setActiveTab,
}) => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedForEnrollment, setSelectedForEnrollment] = useState<string[]>([]);
  const [enrollmentRecords, setEnrollmentRecords] = useState<EnrollmentRecord[]>([]);
  const [showEnrollmentHistory, setShowEnrollmentHistory] = useState(false);

  useEffect(() => {
    const records = JSON.parse(localStorage.getItem('enrollmentRecords') || '[]');
    const userRecords = records.filter((r: EnrollmentRecord) => r.email === user.email);
    setEnrollmentRecords(userRecords);
  }, [user.email]);

  const availableCourses = skillsList.filter(course => !enrolledCourses.includes(course.id));
  const enrolledCourseDetails = skillsList.filter(course => enrolledCourses.includes(course.id));
  const pendingSkills = selectedSkills.filter(skill => !enrolledCourses.includes(skill));

  const totalEnrollmentCost = pendingSkills.length * COURSE_PRICE;
  const totalCVCost = hasPaidForCV ? 0 : CV_DOWNLOAD_PRICE;
  const totalPending = totalEnrollmentCost + totalCVCost;

  const handleEnrollAll = () => {
    if (pendingSkills.length > 0) {
      setSelectedForEnrollment(pendingSkills);
      setShowPaymentModal(true);
    }
  };

  const handleEnrollSingle = (courseId: string) => {
    setSelectedForEnrollment([courseId]);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = (txRef: string, courses: string[], _courseNames: string[]) => {
    onPaymentSuccess(txRef, courses, []);
    setShowPaymentModal(false);
    setSelectedForEnrollment([]);
  };

  const handleCVPayment = () => {
    setSelectedForEnrollment([]);
    setShowPaymentModal(true);
    setPaymentModalOpen(true);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Welcome Banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-600 to-red-600 rounded-3xl p-8 text-white"
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold mb-2">Welcome back, {user.fullName}! 👋</h2>
            <p className="text-blue-100">Your learning journey continues. Track your progress and access your courses.</p>
          </div>
          <div className="flex items-center gap-3 bg-white/20 px-4 py-2 rounded-xl">
            <Mail className="w-4 h-4" />
            <span className="text-sm">{user.email}</span>
          </div>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100"
        >
          <div className="flex items-center justify-between mb-3">
            <BookOpen className="w-8 h-8 text-blue-600" />
            <span className="text-3xl font-bold text-gray-900">{enrolledCourseDetails.length}</span>
          </div>
          <p className="text-gray-600 font-medium">Enrolled Courses</p>
          <p className="text-xs text-gray-400 mt-1">{skillsList.length - enrolledCourseDetails.length} available</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100"
        >
          <div className="flex items-center justify-between mb-3">
            <Star className="w-8 h-8 text-green-600" />
            <span className="text-3xl font-bold text-gray-900">{enrolledCourses.length}</span>
          </div>
          <p className="text-gray-600 font-medium">Skills Mastered</p>
          <p className="text-xs text-gray-400 mt-1">Out of {selectedSkills.length} selected</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100"
        >
          <div className="flex items-center justify-between mb-3">
            <Download className="w-8 h-8 text-purple-600" />
            <span className={`text-3xl font-bold ${hasPaidForCV ? 'text-green-600' : 'text-gray-900'}`}>
              {hasPaidForCV ? '✓' : '$3'}
            </span>
          </div>
          <p className="text-gray-600 font-medium">CV Download</p>
          <p className="text-xs text-gray-400 mt-1">{hasPaidForCV ? 'Unlocked' : 'Locked'}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100"
        >
          <div className="flex items-center justify-between mb-3">
            <CreditCard className="w-8 h-8 text-orange-600" />
            <span className="text-3xl font-bold text-gray-900">${totalPending}</span>
          </div>
          <p className="text-gray-600 font-medium">Pending Payments</p>
          <p className="text-xs text-gray-400 mt-1">{totalPending === 0 ? 'All paid!' : `${pendingSkills.length} courses + CV`}</p>
        </motion.div>
      </div>

      {/* Course Access Section */}
      <div className="grid md:grid-cols-2 gap-8">
        {/* My Courses */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-3xl shadow-2xl overflow-hidden"
        >
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              My Courses
            </h3>
            <p className="text-blue-100 text-sm mt-1">Courses you have access to</p>
          </div>
          <div className="p-6">
            {enrolledCourseDetails.length > 0 ? (
              <div className="space-y-3">
                {enrolledCourseDetails.map((course) => (
                  <motion.div
                    key={course.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${course.color} flex items-center justify-center`}>
                        <i className={`${course.icon} text-white text-lg`}></i>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">{course.name}</p>
                        <p className="text-xs text-gray-500">{course.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-xs font-medium text-green-600">Unlocked</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Lock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-medium">No enrolled courses yet</p>
                <p className="text-gray-400 text-sm">Go to "Choose Skills" to select and enroll in courses</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Locked Courses / Pending Enrollment */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-3xl shadow-2xl overflow-hidden"
        >
          <div className="bg-gradient-to-r from-gray-700 to-gray-800 p-6 text-white">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Locked Courses
            </h3>
            <p className="text-gray-300 text-sm mt-1">Pay $2 per course to unlock</p>
          </div>
          <div className="p-6">
            {availableCourses.length > 0 ? (
              <div className="space-y-3">
                {availableCourses.map((course) => (
                  <motion.div
                    key={course.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${course.color} flex items-center justify-center opacity-60`}>
                        <i className={`${course.icon} text-white text-lg`}></i>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">{course.name}</p>
                        <p className="text-xs text-gray-500">{course.description}</p>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleEnrollSingle(course.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-red-600 text-white rounded-lg text-sm font-semibold hover:shadow-lg transition-all"
                    >
                      <CreditCard className="w-4 h-4" />
                      $2 Enroll
                    </motion.button>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <p className="text-gray-700 text-lg font-medium">All courses enrolled!</p>
                <p className="text-gray-500 text-sm">You've enrolled in all available courses</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Bulk Enrollment CTA */}
      {pendingSkills.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-3xl p-6 md:p-8"
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                Unlock All Pending Courses
              </h3>
              <p className="text-gray-600 mb-3">You have <span className="font-bold text-blue-600">{pendingSkills.length} course(s)</span> waiting to be unlocked</p>
              <div className="flex flex-wrap gap-2">
                {pendingSkills.map(skillId => {
                  const skill = skillsList.find(s => s.id === skillId);
                  return skill ? (
                    <span key={skillId} className="px-3 py-1.5 bg-white border border-gray-200 rounded-full text-sm text-gray-700 flex items-center gap-1">
                      <i className={`${skill.icon} text-blue-600`}></i>
                      {skill.name}
                      <span className="text-xs text-gray-400 ml-1">${COURSE_PRICE}</span>
                    </span>
                  ) : null;
                })}
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-sm text-gray-500 mb-1">Total Cost</p>
              <p className="text-4xl font-bold text-blue-600 mb-3">${totalEnrollmentCost}</p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleEnrollAll}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-red-600 text-white rounded-xl font-bold hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              >
                <CreditCard className="w-5 h-5" />
                Pay & Enroll All
              </motion.button>
              <p className="text-xs text-gray-400 mt-2">
                {pendingSkills.length} × ${COURSE_PRICE} = ${totalEnrollmentCost}
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* CV Download Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className={`bg-white rounded-3xl shadow-2xl overflow-hidden ${hasPaidForCV ? 'border-2 border-green-200' : 'border-2 border-gray-200'}`}
      >
        <div className={`p-6 ${hasPaidForCV ? 'bg-gradient-to-r from-green-600 to-teal-600' : 'bg-gradient-to-r from-gray-700 to-gray-800'} text-white`}>
          <h3 className="text-xl font-bold flex items-center gap-2">
            <Download className="w-5 h-5" />
            CV Download
          </h3>
          <p className={`${hasPaidForCV ? 'text-green-100' : 'text-gray-300'} text-sm mt-1`}>
            {hasPaidForCV ? 'You have access to download your professional CV' : 'Download your professional CV as a clean PDF'}
          </p>
        </div>
        <div className="p-6">
          {hasPaidForCV ? (
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-bold text-gray-900">CV Download Unlocked</span>
                </div>
                <p className="text-gray-600 text-sm">You've already paid the ${CV_DOWNLOAD_PRICE} CV download fee. Go to CV Assistant to download your PDF.</p>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveTab('cv')}
                className="px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-xl font-bold hover:shadow-lg transition-all flex items-center gap-2"
              >
                <Download className="w-5 h-5" />
                Go to CV Assistant
              </motion.button>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-5 h-5 text-gray-600" />
                  <span className="font-bold text-gray-900">CV Download Locked</span>
                </div>
                <p className="text-gray-600 text-sm">Pay a one-time fee of <span className="font-bold text-blue-600">${CV_DOWNLOAD_PRICE}</span> to unlock PDF download. Preview is always free.</p>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleCVPayment}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-red-600 text-white rounded-xl font-bold hover:shadow-lg transition-all flex items-center gap-2"
              >
                <CreditCard className="w-5 h-5" />
                Pay ${CV_DOWNLOAD_PRICE} to Unlock
              </motion.button>
            </div>
          )}
        </div>
      </motion.div>

      {/* Payment History */}
      {enrollmentRecords.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-3xl shadow-2xl overflow-hidden"
        >
          <div 
            className="p-6 bg-gradient-to-r from-gray-100 to-gray-200 cursor-pointer flex items-center justify-between"
            onClick={() => setShowEnrollmentHistory(!showEnrollmentHistory)}
          >
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-600" />
              Payment History
            </h3>
            <span className="text-sm text-gray-500">{enrollmentRecords.length} transaction(s)</span>
          </div>
          {showEnrollmentHistory && (
            <div className="p-6 space-y-3">
              {enrollmentRecords.map((record, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <p className="font-bold text-gray-900">{record.courses.join(', ')}</p>
                    <p className="text-xs text-gray-500">{new Date(record.paidAt).toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-600">${record.amount}</p>
                    <p className="text-xs text-gray-400">{record.tx_ref}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedForEnrollment([]);
          }}
          onPaymentSuccess={handlePaymentSuccess}
          selectedCourses={selectedForEnrollment}
          skillsList={skillsList}
          userEmail={user.email}
          userName={user.fullName}
          type="enrollment"
        />
      )}
    </div>
  );
};

export default UserDashboard;
